import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import { Button } from './Button';
import { User, Save, ArrowLeft, Loader2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const Profile: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  // We only track editable fields in state now
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    address: user?.shippingAddress?.address || '',
    pincode: user?.shippingAddress?.pincode || '',
    state: user?.shippingAddress?.state || '',
    district: user?.shippingAddress?.district || ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleInputChange = (field: string, value: string) => {
    if (field === 'pincode') {
      value = value.replace(/\D/g, '').slice(0, 6);
    } else if (field === 'firstName' || field === 'lastName') {
      value = value.slice(0, 15);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      showNotification('Name is required', 'error');
      return;
    }

    try {
      setIsSaving(true);
      
      // STRICT MAPPING: We only update name and address. 
      // Numbers are pulled directly from the existing user object so they never overwrite.
      const updatedUser = {
        ...user,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        mobile: user.mobile, // Locked
        alternativeMobile: user.alternativeMobile, // Locked
        shippingAddress: {
          fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          address: formData.address.trim(),
          pincode: formData.pincode.trim(),
          state: formData.state.trim(),
          district: formData.district.trim(),
          mobile: user.mobile // Always use the verified primary mobile for shipping
        }
      };

      // Since you use Email as PK, prioritize email, fallback to uid or mobile
      const docId = user.email || user.uid || user.mobile;
      
      await updateDoc(doc(db, 'users', docId), updatedUser);
      updateUserProfile(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      showNotification('Profile updated successfully', 'success');

      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error: any) {
      console.error('Profile update error:', error);
      showNotification(error.message || 'Failed to update profile', 'error');
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl animate-[fadeIn_0.3s_ease-out]">
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-900 mb-8 transition-colors"
      >
        <ArrowLeft size={18} /> Back
      </button>

      <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
        {/* Header Area */}
        <div className="p-6 md:p-8 border-b border-stone-100 bg-stone-50">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-white border border-stone-200 rounded-full flex items-center justify-center text-stone-600 shadow-sm">
              <User size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-stone-900 mb-1">Account Profile</h1>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Personal Information</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="p-6 md:p-8 space-y-6">
          
          {/* Names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                required
              />
            </div>
          </div>

          {/* Contact Numbers (Locked) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-stone-50 rounded-xl border border-stone-100">
            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                 <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Primary Mobile</label>
                 <Lock size={12} className="text-stone-400" />
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium text-sm">+91</span>
                <input
                  type="tel"
                  value={user?.mobile || ''}
                  disabled
                  className="w-full pl-12 pr-4 py-3 border border-stone-200 rounded-xl text-sm font-medium bg-stone-100/50 text-stone-500 cursor-not-allowed outline-none"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                 <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Alternative Mobile</label>
                 <Lock size={12} className="text-stone-400" />
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium text-sm">+91</span>
                <input
                  type="tel"
                  value={user?.alternativeMobile || ''}
                  disabled
                  placeholder="Not provided"
                  className="w-full pl-12 pr-4 py-3 border border-stone-200 rounded-xl text-sm font-medium bg-stone-100/50 text-stone-500 cursor-not-allowed outline-none"
                />
              </div>
            </div>
            
            <div className="col-span-full">
               <p className="text-xs text-stone-500 font-medium">
                 Phone numbers are locked for security. Please contact our support team if you need to update your registered contact information.
               </p>
            </div>
          </div>

          {/* Address Section */}
          <div className="pt-6 border-t border-stone-100">
            <h3 className="text-sm font-serif text-stone-900 mb-6">Default Shipping Destination</h3>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">Full Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="House No, Building, Street Area"
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">City/District</label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 ml-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <Button 
              type="submit" 
              disabled={isSaving}
              className="w-full py-4 rounded-xl text-sm font-semibold shadow-md flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
